import { Component, OnInit, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { PARAM_LABEL_INTERVENANT } from 'src/app/core/services/constant/constants.service';
import { FacadeService } from 'src/app/core/services/facade.service';
import { Address } from 'src/app/shared/models/address';
import { ErrorResponse } from 'src/app/shared/models/api-response/error-response';
import { CareSkill } from 'src/app/shared/models/employee/care-skill';
import { Employee } from 'src/app/shared/models/employee/employee';
import {
	EmployeeType,
	EmployeeTypeString
} from 'src/app/shared/models/employee/employee-type';
import { ConvertObjectToCalendarEvent } from 'src/app/shared/utils/convert-object-to-calendar-event';

@Component({
	selector: 'app-detail-employee',
	templateUrl: './detail-employee.component.html',
	styleUrls: ['./detail-employee.component.scss']
})
export class DetailEmployeeComponent implements OnInit {
	MAX_NAME_CHAR = 25;
	NAME_PATTERN = new RegExp("^[0-9a-zA-Z'éèàç._-]+( [0-9a-zA-Z'éèàç._-]+)*$");

	employee: Employee = null;
	confirmDeleteModalRef: BsModalRef;
	competences;
	isDeletable: boolean;

	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isSAE = false;

	isAreaActive = false;

	labelIntervenantCapitalized: string;

	isFormReadOnly = true;

	employeeDetailForm = new FormGroup({
		firstName: new FormControl('', [
			Validators.required,
			Validators.maxLength(this.MAX_NAME_CHAR),
			Validators.pattern(this.NAME_PATTERN)
		]),
		lastName: new FormControl('', [
			Validators.required,
			Validators.maxLength(this.MAX_NAME_CHAR),
			Validators.pattern(this.NAME_PATTERN)
		]),
		address: new FormControl(new Address().label, [
			Validators.required,
			Validators.maxLength(60)
		]),
		workplaceAddress: new FormControl(new Address().label, [
			Validators.required,
			Validators.maxLength(60)
		]),
		birthDate: new FormControl<Date>(null),
		phone: new FormControl('', Validators.pattern('0{1}[0-9]{9}')),
		autoAssignment: new FormControl(),
		workingHoursPerWeek: new FormControl(null, [
			Validators.required,
			Validators.min(1),
			Validators.max(60)
		]),
		employeeType: new FormControl('', Validators.required),
		email: new FormControl('', Validators.email)
		// area: new FormControl('Non spécifié')
	});

	isAddressSelected: { value: boolean } = { value: false };
	isAddressDefault: { value: boolean } = { value: true };
	isWorkplaceAddressSelected: { value: boolean } = { value: false };
	isWorkplaceAddressDefault: { value: boolean } = { value: true };

	//Boolean empêchant l'utilisateur d'envoyer plusieurs fois le formulaire
	isValidateButtonClicked: { value: boolean } = { value: false };

	//Employé.e extérieur
	isEmpExt = false;
	colorsPastel;

	public EmployeeTypeStringEnum = EmployeeTypeString;
	public EmployeeType = EmployeeType;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private modalService: BsModalService,
		private facade: FacadeService,
		private convertObjectToCalendarEvent: ConvertObjectToCalendarEvent
	) {}

	ngOnInit(): void {
		this.colorsPastel = this.convertObjectToCalendarEvent.colorsPastel;

		this.isAreaActive = this.facade.dataStorageService.isAreaActive();
		this.labelIntervenantCapitalized =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_INTERVENANT
			);
		this.isDeletable = this.facade.dataStorageService.isPlanificatrice();

		this.isSESSAD = this.facade.dataStorageService.isSESSAD();
		this.isSAAD = this.facade.dataStorageService.isSAAD();
		this.isSSIAD = this.facade.dataStorageService.isSSIAD();
		this.isSAE = this.facade.dataStorageService.isSAE();
		this.facade.careSkillService
			.getAllCareSkills()
			.subscribe((careSkills: CareSkill[]) => (this.competences = careSkills));

		const id = +this.route.snapshot.paramMap.get('id');
		this.facade.employeeService
			.getEmployeeById(id)
			.subscribe((employee: Employee) => {
				this.employee = employee;
				this.isEmpExt = this.employee.type === EmployeeType.EXT;
				if (
					this.employee.address !== null &&
					this.employee.address.label !== null &&
					this.employee.address.label !== ''
				) {
					this.isAddressDefault.value = true;
				}
				if (
					this.employee.workplaceAddress !== null &&
					this.employee.workplaceAddress.label !== null &&
					this.employee.workplaceAddress.label !== ''
				) {
					this.isWorkplaceAddressDefault.value = true;
				}
				this.resetForm();
			});
	}

	goBack(): void {
		this.router.navigate(['/gestion/employees/list']);
	}

	getEmployeeSkillsAsString(employee: any): string {
		if (!employee || !employee.careSkills) {
			return 'Sans';
		}

		const skills = employee.careSkills;
		if (skills.length > 0) {
			return skills.map((skill) => skill.name || 'Sans').join(' | ');
		}

		return 'Sans';
	}

	hasValidColor(): boolean {
		return (
			this.employee &&
			this.employee.colorIndex !== undefined &&
			this.employee.colorIndex !== null &&
			this.colorsPastel &&
			this.colorsPastel[this.employee.colorIndex] !== undefined
		);
	}

	delete(employee: Employee): void {
		if (this.isDeletable) {
			this.facade.employeeService.deleteEmployee(employee.id).subscribe({
				complete: () => {
					this.modalService.hide();
					this.goBack();
				},
				error: (err: ErrorResponse) => {
					this.facade.toastService.createHttpErrorToast(
						err.error.response ? err.error.response : '',
						err.status
					);
					this.modalService.hide();
				}
			});
		}
	}

	openConfirmDeleteModal(confirmDeleteTemplate: TemplateRef<any>): void {
		this.confirmDeleteModalRef = this.modalService.show(confirmDeleteTemplate);
	}

	editForm() {
		this.isFormReadOnly = false;
	}

	stopEditForm(isSubmit: boolean) {
		this.isFormReadOnly = true;
		this.isValidateButtonClicked.value = false;

		if (isSubmit) {
			this.facade.employeeService.getEmployeeById(this.employee.id).subscribe({
				next: (employee: Employee) => {
					this.employee = employee;
					this.resetForm();
				},
				error: (err) => {
					this.isValidateButtonClicked.value = false;
					this.facade.toastService.createHttpErrorToast(
						err.error.response ? err.error.response : '',
						err.status
					);
					this.resetForm();
				}
			});
		}
	}

	onSubmit() {
		if (
			this.employeeDetailForm.valid &&
			(this.isAddressSelected.value || this.isAddressDefault.value)
		) {
			this.facade.employeeService.updateEmployee(this.employee).subscribe({
				complete: () => {
					// this.facade.toastService.createSuccessToast();
					this.stopEditForm(true);
				},
				error: (err) => {
					this.isValidateButtonClicked.value = false;
					this.facade.toastService.createHttpErrorToast(
						err.error.response ? err.error.response : '',
						err.status
					);
					// On va chercher la nouvelle version de l'employé
					this.facade.employeeService
						.getEmployeeById(this.employee.id)
						.subscribe((employee: Employee) => {
							this.employee = employee;
							this.resetForm();
						});
				}
			});
		} else {
			this.isValidateButtonClicked.value = false;
			// this.facade.toastService.createErrorToast();
		}
	}

	resetForm() {
		this.employeeDetailForm.reset();
		this.employeeDetailForm.setValue({
			firstName: this.employee.firstName,
			lastName: this.employee.lastName,
			address: this.employee.address.label,
			workplaceAddress: this.employee.workplaceAddress.label,
			birthDate:
				this.employee.birthDate !== null && this.employee.birthDate !== ''
					? new Date(this.employee.birthDate)
					: null,
			phone: this.employee.phone,
			autoAssignment: this.employee.autoAssignment,
			workingHoursPerWeek: this.employee.workingHoursPerWeek,
			employeeType: this.employee.type,
			email: this.employee.email
			// area:
			// 	this.employee.areas && this.employee.areas.length > 0
			// 		? this.employee.areas[0].name
			// 		: 'Non spécifié'
		});
	}

	specialitiesAsString() {
		let str = '';
		this.employee.specialties
			.sort((a, b) => a.name.localeCompare(b.name))
			.forEach((e) => (str += e.name + ', '));
		return str.slice(0, -2);
	}

	get firstName() {
		return this.employeeDetailForm.get('firstName');
	}
	get lastName() {
		return this.employeeDetailForm.get('lastName');
	}
	get address() {
		return this.employeeDetailForm.get('address');
	}
	get workplaceAddress() {
		return this.employeeDetailForm.get('workplaceAddress');
	}
	get competence() {
		return this.employeeDetailForm.get('competence');
	}
	get phone() {
		return this.employeeDetailForm.get('phone');
	}
	get workingHoursPerWeek() {
		return this.employeeDetailForm.get('workingHoursPerWeek');
	}
	get employeeType() {
		return this.employeeDetailForm.get('employeeType');
	}
	get autoAssignment() {
		return this.employeeDetailForm.get('autoAssignment');
	}
	get email() {
		return this.employeeDetailForm.get('email');
	}
}
